import React, { useEffect, useMemo, useRef } from 'react'
import { useNavigate } from 'react-router-dom'

import { useToast } from '@pnld/components-web'

import {
  useQueryGetCitiesByUf,
  useQueryGetEducationStages,
  useQueryGetPredictionsYears,
  useQueryGetSchoolsByCity,
} from '@/api/auxiliaries/queries'
import type { TGetEducationStagesResponse } from '@/api/auxiliaries/types'
import {
  useQueryGetCheckPredictions,
  useQueryGetPredictionsPreview,
} from '@/api/predictions/queries'
import type { TGetPredictionParams } from '@/api/predictions/types'
import { sphereOptions } from '@/data/dashboard'
import { getErrorDetails } from '@/utils/error'
import {
  formatEducationStages,
  formatEducationStagesByIds,
} from '@/utils/formatFilters'

const emptySearch = {
  qtCensus: 0,
  qtH1: 0,
  qtH2: 0,
  qtEntities: 0,
  teachingSteps: {},
}

export const allSpheresValues = ['PUBLICA', 'PRIVADA', 'PRIVADA_CONVENIADA']
export const allStagesValues = [
  'CURSO_TECNICO',
  'EDUCACAO_INFANTIL',
  'EJA',
  'ENSINO_FUNDAMENTAL_INICIAL',
  'ENSINO_FUNDAMENTAL_FINAL',
  'ENSINO_MEDIO',
]

export const usePredictionsController = () => {
  const navigate = useNavigate()
  const toast = useToast()
  const checkFirstRender = useRef(true)
  const [availableUntilYear, setAvailableUntilYear] = React.useState<number>(0)
  const [isSearchEmpty, setIsSearchEmpty] = React.useState(false)
  const [predictionsFilters, setPredictionsFilters] = React.useState<
    TGetPredictionParams & { isAllStages: boolean }
  >({
    coEducationStage: '',
    nuCensusYear: '',
    nuVersao: undefined as number | string | undefined,
    coSphere: [] as number[],
    isAllStages: false,
    stPnldService: false,
    coRegion: [] as string[],
    coCity: '',
    coUf: '',
    tpLocation: '',
    coEntity: '',
    tpDependency: '',
  })
  const [regionInfo, setRegionInfo] = React.useState({
    sgUf: '',
    noCity: '',
    noSchool: '',
  })
  const {
    data: predictionsPreview,
    refetch: refetchPredictionsPreview,
    isFetching: isPredictionsPreviewLoading,
  } = useQueryGetPredictionsPreview(predictionsFilters, {
    enabled: false,
    onSuccess: data => {
      if (JSON.stringify(data) === JSON.stringify(emptySearch)) {
        setIsSearchEmpty(true)
        return
      }
      let spheres = sphereOptions.map(o => o.label).join(', ')

      if (
        Array.isArray(predictionsFilters.coSphere) &&
        predictionsFilters.coSphere.length
      ) {
        spheres = predictionsFilters.coSphere
          .map((code: number) => {
            const opt = sphereOptions.find(o => o.value === Number(code))
            return opt ? opt.label : String(code)
          })
          .join(', ')
      }
      const stages = getStagesFiltered(
        predictionsFilters.isAllStages,
        educationStages
      )

      navigate('/predicoes/resultado', {
        state: {
          predictionsPreview: data,
          filters: predictionsFilters,
          spheres,
          educationStages: stages,
          location: predictionsFilters.tpLocation,
          dependency: predictionsFilters.tpDependency,
        },
      })
    },
    onError: error => {
      toast({
        status: 'error',
        title: 'Ops! Ocorreu um erro',
        description: getErrorDetails(error),
        isClosable: true,
      })
    },
  })
  const {
    data: checkPredictionsData,
    refetch: refetchCheckPredictions,
    isFetching: isCheckingPredictions,
  } = useQueryGetCheckPredictions({
    enabled: true,
    onError: error => {
      toast({
        status: 'error',
        title: 'Ops! Ocorreu um erro',
        description: getErrorDetails(error),
        isClosable: true,
      })
    },
  })
  const { data: educationStages, isLoading: isEducationStagesLoading } =
    useQueryGetEducationStages({
      onError: error => {
        toast({
          status: 'error',
          title: 'Ops! Ocorreu um erro',
          description: getErrorDetails(error),
          isClosable: true,
        })
      },
    })
  const { data: predictionYears, isLoading: isPredictionYearsLoading } =
    useQueryGetPredictionsYears({
      onError: error => {
        toast({
          status: 'error',
          title: 'Ops! Ocorreu um erro',
          description: getErrorDetails(error),
          isClosable: true,
        })
      },
    })
  const { data: cities, isFetching: isCitiesLoading } = useQueryGetCitiesByUf(
    {
      sgUf: regionInfo.sgUf,
    },
    {
      enabled: !!regionInfo.sgUf,
      onError: error => {
        toast({
          status: 'error',
          title: 'Ops! Ocorreu um erro',
          description: getErrorDetails(error),
          isClosable: true,
        })
      },
    }
  )
  const { data: schools, isFetching: isSchoolsLoading } =
    useQueryGetSchoolsByCity(
      {
        noCity: regionInfo.noCity,
      },
      {
        enabled: !!regionInfo.noCity,
        onError: error => {
          toast({
            status: 'error',
            title: 'Ops! Ocorreu um erro',
            description: getErrorDetails(error),
            isClosable: true,
          })
        },
      }
    )

  const getStagesFiltered = (
    allStages: boolean,
    educationStagesData?: TGetEducationStagesResponse
  ) => {
    if (predictionsFilters.coEducationStage) {
      if (educationStagesData) {
        const idToNameMap: Record<string, string> = {}
        Object.values(educationStagesData).forEach(stages =>
          stages.forEach(
            stage =>
              (idToNameMap[String(stage.co_education_stage)] =
                stage.no_education_stage)
          )
        )

        return formatEducationStagesByIds(
          predictionsFilters.coEducationStage,
          idToNameMap
        )
      }

      return predictionsFilters.coEducationStage
    }

    if (predictionsFilters.stPnldService) {
      return 'Etapas atendidas pelo PNLD'
    }

    if (allStages && !predictionsFilters.stPnldService) {
      return 'Todas as etapas de ensino'
    }

    return allStages ? formatEducationStages(allStagesValues.join(',')) : ''
  }

  useEffect(() => {
    if (checkFirstRender.current) {
      checkFirstRender.current = false
      return
    }

    refetchPredictionsPreview()
  }, [predictionsFilters])

  const citiesByUf = useMemo(() => {
    if (!cities) return []

    return (cities as Array<{ value: string; label: string }>).map(city => ({
      label: city.label,
      value: city.value,
    }))
  }, [cities])

  const schoolsByCity = useMemo(() => {
    if (!schools) return []

    return (schools as Array<{ value: string; label: string }>).map(school => ({
      label: school.label,
      value: school.value,
    }))
  }, [schools])

  const predictionYearsOptions = useMemo(() => {
    const maxYear = Math.max(...(predictionYears || [0]))
    setAvailableUntilYear(maxYear)

    return (predictionYears || []).map(year => ({
      label: String(year),
      value: year,
    }))
  }, [predictionYears])

  return {
    predictionsPreview,
    refetchPredictionsPreview,
    isPredictionsPreviewLoading,
    educationStages,
    isEducationStagesLoading,
    availableUntilYear,
    predictionYearsOptions,
    isPredictionYearsLoading,
    setPredictionsFilters,
    isSearchEmpty,
    setIsSearchEmpty,
    checkPredictionsData,
    refetchCheckPredictions,
    isCheckingPredictions,
    regionInfo,
    setRegionInfo,
    schoolsByCity,
    isCitiesLoading,
    citiesByUf,
    isSchoolsLoading,
  }
}
